import React, { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useParams } from "next/navigation";
import { LuCheck, LuX } from "react-icons/lu";
import { useQuery } from "@tanstack/react-query";
import { useWhiteLabel, usePermissionsContext } from "~/app/providers";
import { slugifyUser } from "~/utils/string";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import UserImage from "~/components/users/UserImage";
import { toastHandler } from "~/components/ToastHandler";
import { generateCheckinQRCode } from "~/app/api/actions/qr-generator";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetClose,
} from "../shadcn/Sheet";
import { Button } from "../themed/Button";
import Modal from "../themed/Modal";
import {
    RiQuestionAnswerLine,
    RiQuestionnaireLine,
    RiSearchLine,
} from "react-icons/ri";
import { Input } from "../themed/Input";
import Fuse from "fuse.js";
import { If } from "../Conditional";
import { Badge } from "~/components/shadcn/Badge";
import {
    getEventChapterPreviousAttendanceCounts,
    getEventMembershipStatusForUsers,
} from "~/app/events/[slug]/actions";
import { CountBadge } from "./CountBadge";
import { useSession } from "next-auth/react";
import { BsQrCodeScan } from "react-icons/bs";
import BarcodeScanner, { CheckinState } from "./BarcodeScanner";
import { toast } from "react-toastify";
import { PermissionEnum } from "~/types/PermissionTypes";
import { updateAttendeesCheckInStatus } from "~/app/_actions/attendee";
import { ScrollableContainer } from "~/app/beta/home/_components/ScrollableContainer";
import { generateRandomName } from "~/utils/constants";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { cn } from "~/utils/fontsConfig";

type Props = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    attendingCount: number;
    event: EventBySlugOutput;
    users?: (
        | {
              name: string | null;
              id: string;
              email: string | null;
              socialLinks: PrismaJson.SocialLinks;
              bio: string | null;
              image: string | null;
              slug: string | null;
              questionnaireCompletions?: {
                  question: string;
                  answer: string;
              }[];
              ticketPurchase?: {
                  quantity: number;
                  eventTicket: {
                      name: string;
                      description: string | null;
                      price: number | null;
                  };
              }[];
          }
        | {
              id: string;
              name: string | null;
              image: string | null;
              slug: string | null;
              checkedIn: boolean;
              questionnaireCompletions?: {
                  question: string;
                  answer: string;
              }[];
              ticketPurchase?: {
                  quantity: number;
                  eventTicket: {
                      name: string;
                      description: string | null;
                      price: number | null;
                  };
              }[];
          }
    )[];
    refetchEvent: () => void;
    fromBetaPages?: boolean;
};

export const EventAttendeesSheet = ({
    isOpen,
    setIsOpen,
    event,
    users,
    attendingCount,
    refetchEvent,
    fromBetaPages = false,
}: Props) => {
    const { data: session } = useSession();
    if (!users) return;
    const permissions = usePermissionsContext();
    const customization = useWhiteLabel()?.customization;

    const isAuthorized =
        permissions.isAdmin ||
        permissions.isManager ||
        permissions.isHost ||
        permissions.rbac[PermissionEnum.CHECKIN_ATTENDEES];

    const [visibilityMap, setVisiblityMap] = useState(
        Object.fromEntries(users.map((u) => [u.id, false])),
    );

    const params = useParams();
    const slug = params?.slug ?? "";
    const pathSlug = Array.isArray(slug) ? slug[0] : slug;

    const [searchTerm, setSearchTerm] = useState("");
    const [showQRCodeModal, setShowQRCodeModal] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const fuse = useMemo(
        () =>
            new Fuse(users, {
                keys: ["name", "email"],
                threshold: 0.3,
                ignoreLocation: true,
                shouldSort: true,
            }),
        [users],
    );

    const { data: membershipStatuses = {} } = useQuery({
        queryKey: [
            "membershipStatuses",
            event.communityId,
            users?.map((u) => u.id),
        ],
        queryFn: async () => {
            if (!users || !event.communityId) return {};
            const userIds = users.map((user) => user.id);
            return getEventMembershipStatusForUsers(userIds, {
                isMembersOnlyTagGated: event.isMembersOnlyTagGated,
                resourceTags: event.resourceTags,
                communityId: event.communityId,
            });
        },
        enabled:
            !!users &&
            !!event.communityId &&
            !!session?.user.id &&
            (permissions.isHost || permissions.isManager) &&
            isOpen,
    });

    const { data: chapterAttendanceCounts = {} } = useQuery({
        queryKey: ["chapterAttendance", event.id, users?.map((u) => u.id)],
        queryFn: async () => {
            if (!isAuthorized) return {};
            if (!users || !event.communityId || !event.id) return {};
            return getEventChapterPreviousAttendanceCounts({
                eventId: event.id,
                userIds: users.map((user) => user.id),
            });
        },
        enabled:
            !!users &&
            !!event.communityId &&
            !!event.id &&
            !!session?.user.id &&
            (permissions.isHost || permissions.isManager) &&
            isOpen,
    });

    // get filtered users based on search term
    const displayedUsers = useMemo(() => {
        if (!searchTerm.trim()) return users;
        return fuse.search(searchTerm).map((result) => result.item);
    }, [searchTerm, users, fuse]);

    const { data: qrCodeUrl } = useQuery(
        ["qrCode", event.id],
        () => {
            return generateCheckinQRCode(event, event.community);
        },
        {
            enabled: !!event && isOpen,
        },
    );

    const hostIds = event.hosts.map((host) => host.user.id);
    const checkIfHost = (id: string) => {
        return hostIds.includes(id);
    };

    const checkInUser = async (user: (typeof users)[0]) => {
        await toastHandler({
            loadingMessage: "Checking in User...",
            successMessage: "User checked in successfully!",
            errorMessage: "Failed to check in user",
            operation: () =>
                updateAttendeesCheckInStatus({
                    userIds: [user.id],
                    eventId: event.id,
                    checkin: true,
                }),
            onSettled: () => {
                refetchEvent();
            },
        });
    };
    const checkOutUser = async (user: (typeof users)[0]) => {
        await toastHandler({
            loadingMessage: "Checking out user...",
            successMessage: "User checked out successfully",
            errorMessage: "Error checking out user",
            operation: () =>
                updateAttendeesCheckInStatus({
                    userIds: [user.id],
                    eventId: event.id,
                    checkin: false,
                }),
            onSettled: () => {
                refetchEvent();
            },
        });
    };

    const handleToggleQuestionnaire = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>, userId: string) => {
            if (!users.find((u) => u.id === userId)?.questionnaireCompletions)
                return false;
            setVisiblityMap({
                ...visibilityMap,
                [userId]: !visibilityMap[userId],
            });
            e.stopPropagation();
            e.preventDefault();
        },
        [visibilityMap],
    );

    const toggleScanner = () => {
        setShowScanner(!showScanner);
    };

    // Keep track of last 10 scanned codes with timestamps
    const [recentScans] = useState<Array<{ code: string; timestamp: number }>>(
        [],
    );
    const handleCodeScanned = async (
        code: string,
    ): Promise<CheckinState | undefined> => {
        const now = Date.now();
        const recentScan = recentScans.find((scan) => scan.code === code);

        if (recentScan && now - recentScan.timestamp < 1000) return undefined;

        recentScans.unshift({ code, timestamp: now });
        if (recentScans.length > 10) recentScans.pop();

        const [type, userId, suffix] = code.split(".");
        const user = users.find((u) => u.id === userId);
        if (user && "checkedIn" in user && !user.checkedIn) {
            user.checkedIn = true;
            checkInUser(user);
            toast.success(`Checked in ${user.name}`);
            return CheckinState.VALID;
        }
        if (user && "checkedIn" in user && user.checkedIn) {
            toast.warning(`${user.name} is already checked in`);
            return CheckinState.ALREADY_CHECKED_IN;
        }
        return CheckinState.INVALID;
    };

    return (
        <Sheet
            open={isOpen}
            onOpenChange={() => {
                setShowScanner(false);
                setIsOpen(false);
            }}
        >
            <SheetContent
                showCloseIcon={false}
                className={cn(
                    "flex h-full w-full flex-col rounded-br-md rounded-tr-md border-none",
                    customization?.mainCardClassName || "bg-[#121418]",
                    "md:min-w-[450px]",
                )}
            >
                <SheetHeader className="flex-none">
                    <div className="flex w-full items-center justify-between px-6 py-4">
                        <SheetTitle
                            className={cn(
                                "text-lg",
                                customization?.labelClassName,
                            )}
                        >
                            List of attendees
                        </SheetTitle>
                        <SheetClose className="focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                            <LuX className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>
                </SheetHeader>

                <Modal
                    open={showQRCodeModal}
                    onOpenChange={() => setShowQRCodeModal(false)}
                >
                    <div className="flex items-center justify-center">
                        {qrCodeUrl && (
                            <Image
                                width={250}
                                height={250}
                                src={qrCodeUrl}
                                alt="QR Code"
                                priority
                            />
                        )}
                    </div>
                </Modal>

                <div className="flex h-full flex-1 flex-col overflow-hidden">
                    <div className="mb-2 flex-none px-6">
                        <div
                            className={cn(
                                "text-base",
                                customization?.descriptionClassName,
                            )}
                        >
                            {attendingCount} people will attend
                        </div>
                        <Input
                            placeholder="Search by name or email"
                            className={cn(
                                "mt-1 w-full",
                                customization?.inputClassName,
                            )}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            prefixComponent={
                                <RiSearchLine className="mr-2 h-4 w-4" />
                            }
                        />
                    </div>

                    <ScrollableContainer
                        wrapperClassName="min-h-0 flex-1 h-full"
                        className="absolute bottom-0 top-0 h-full w-full overflow-y-auto px-6 pb-4 pt-6"
                        topShadowColor="from-black/80 to-transparent"
                    >
                        <div className="space-y-5">
                            {!showScanner &&
                                displayedUsers.map((user, index) => (
                                    <div key={user?.id ?? "" + index}>
                                        <div
                                            className={cn(
                                                "flex items-center justify-between",
                                                customization?.descriptionClassName,
                                            )}
                                        >
                                            <div className="flex w-full items-center gap-4">
                                                <Link
                                                    href={`/profile/${slugifyUser(
                                                        {
                                                            name: user.name,
                                                            id: user.id,
                                                            slug: user.slug,
                                                        },
                                                    )}`}
                                                    target="_blank"
                                                >
                                                    <UserAvatar
                                                        user={user}
                                                        className="h-9 w-9"
                                                    />
                                                </Link>
                                                <Link
                                                    href={`/profile/${slugifyUser(
                                                        {
                                                            name: user.name,
                                                            id: user.id,
                                                            slug: user.slug,
                                                        },
                                                    )}`}
                                                    className="flex items-center gap-4"
                                                    target="_blank"
                                                    onClick={(e) =>
                                                        handleToggleQuestionnaire(
                                                            e,
                                                            user.id,
                                                        )
                                                    }
                                                >
                                                    <div className="flex max-w-full items-center gap-2 ">
                                                        <span className="text-base">
                                                            {user.name ??
                                                                generateRandomName(
                                                                    user.id,
                                                                )}
                                                        </span>
                                                        <div className="flex w-full max-w-[100px] flex-row items-center justify-between">
                                                            <div className="w-[75px]">
                                                                <If
                                                                    condition={
                                                                        !!user.questionnaireCompletions
                                                                    }
                                                                >
                                                                    <RiQuestionAnswerLine className="h-4 w-4" />
                                                                </If>
                                                            </div>

                                                            <div className="w-[75px]">
                                                                <If
                                                                    condition={
                                                                        !!user.id &&
                                                                        (chapterAttendanceCounts[
                                                                            user
                                                                                .id
                                                                        ] ??
                                                                            0) >
                                                                            0
                                                                    }
                                                                >
                                                                    <CountBadge
                                                                        count={
                                                                            chapterAttendanceCounts[
                                                                                user
                                                                                    .id
                                                                            ]
                                                                        }
                                                                    />
                                                                </If>
                                                            </div>

                                                            <div className="w-[75px]">
                                                                <If
                                                                    condition={
                                                                        !!membershipStatuses?.[
                                                                            user
                                                                                .id
                                                                        ]
                                                                    }
                                                                >
                                                                    <Badge
                                                                        variant="secondary"
                                                                        className="ml-2 px-1.5 py-0.5"
                                                                    >
                                                                        M
                                                                    </Badge>
                                                                </If>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Link>
                                            </div>
                                            {isAuthorized &&
                                                "checkedIn" in user && (
                                                    <>
                                                        <If
                                                            condition={
                                                                !user.checkedIn
                                                            }
                                                        >
                                                            <Button
                                                                variant="secondary"
                                                                size="md"
                                                                className="flex items-center gap-1 text-nowrap"
                                                                onClick={() =>
                                                                    checkInUser(
                                                                        user,
                                                                    )
                                                                }
                                                            >
                                                                <LuCheck className="h-4 w-4" />
                                                                <span className="text-xs">
                                                                    Check in
                                                                </span>
                                                            </Button>
                                                        </If>
                                                        <If
                                                            condition={
                                                                user.checkedIn
                                                            }
                                                        >
                                                            <Button
                                                                variant="secondary"
                                                                size="md"
                                                                className="flex items-center gap-1 text-nowrap"
                                                                onClick={() =>
                                                                    checkOutUser(
                                                                        user,
                                                                    )
                                                                }
                                                            >
                                                                <LuX className="h-4 w-4" />
                                                                <span className="text-xs">
                                                                    Undo Check
                                                                    In
                                                                </span>
                                                            </Button>
                                                        </If>
                                                    </>
                                                )}

                                            {checkIfHost(user.id) && (
                                                <span
                                                    className={cn(
                                                        "rounded-lg px-2 py-1 text-[12px]",
                                                        customization?.buttons
                                                            ?.secondary ||
                                                            "bg-stone-600",
                                                    )}
                                                >
                                                    Host
                                                </span>
                                            )}
                                        </div>

                                        {visibilityMap[user.id] &&
                                            user.ticketPurchase &&
                                            user.ticketPurchase.length > 0 && (
                                                <div className="mt-2 flex flex-col gap-2 rounded-md bg-v3-N-600 p-2">
                                                    <div className="text-sm font-medium text-v3-N-300">
                                                        Tickets Purchased
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        {user.ticketPurchase.map(
                                                            (tp, i) => (
                                                                <div
                                                                    key={i}
                                                                    className="bg-v3-N-700/50 flex flex-col gap-1 rounded-lg p-1"
                                                                >
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-sm text-v3-N-200">
                                                                            {
                                                                                tp
                                                                                    .eventTicket
                                                                                    .name
                                                                            }
                                                                        </span>
                                                                        <span className="text-sm text-v3-N-400">
                                                                            {
                                                                                tp.quantity
                                                                            }{" "}
                                                                            ×{" "}
                                                                            {tp
                                                                                .eventTicket
                                                                                .price ===
                                                                            null
                                                                                ? "Free"
                                                                                : `$${tp.eventTicket.price}`}
                                                                        </span>
                                                                    </div>
                                                                    {tp
                                                                        .eventTicket
                                                                        .description && (
                                                                        <p className="text-sm text-v3-N-400">
                                                                            {
                                                                                tp
                                                                                    .eventTicket
                                                                                    .description
                                                                            }
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                        {visibilityMap[user.id] && (
                                            <ul className="mt-4 flex flex-col gap-2 border-b border-v3-N-450 pb-2 text-xs">
                                                {user.questionnaireCompletions?.map(
                                                    (qc, i) => (
                                                        <li
                                                            key={i}
                                                            className="flex flex-col"
                                                        >
                                                            <span className="flex items-center gap-1 text-v3-N300">
                                                                <RiQuestionnaireLine className="h-3 w-3" />
                                                                {qc.question}
                                                            </span>
                                                            <span className="flex items-center gap-1 text-sm">
                                                                <RiQuestionAnswerLine className="h-3 w-3" />
                                                                {qc.answer}
                                                            </span>
                                                        </li>
                                                    ),
                                                )}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                        </div>
                    </ScrollableContainer>

                    {showScanner && (
                        <BarcodeScanner
                            onCodeScanned={handleCodeScanned}
                            toggleScanner={toggleScanner}
                        />
                    )}

                    {!showScanner && isAuthorized && (
                        <div className="flex w-full flex-none justify-end space-x-2 border-t border-v3-N-450 px-6 py-4">
                            <Button
                                className={cn(
                                    "w-full text-xs",
                                    customization?.buttons?.default,
                                )}
                                onClick={toggleScanner}
                                variant={showScanner ? "default" : "tertiary"}
                            >
                                <BsQrCodeScan />
                                <span>
                                    {showScanner
                                        ? "Stop Scanning"
                                        : "Scan QR Code"}
                                </span>
                            </Button>
                            <Button
                                variant="default"
                                className="w-full text-xs"
                            >
                                <Link
                                    href={
                                        !fromBetaPages
                                            ? `${pathSlug}/attendees`
                                            : `/beta/events/${event.slug}/manage/guests`
                                    }
                                >
                                    {!fromBetaPages
                                        ? "Attendee Management"
                                        : "Guests Management"}
                                </Link>
                            </Button>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};
